import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BriefcaseIcon,
  MapPinIcon,
  SparklesIcon,
  HeartIcon,
  BookOpenIcon,
  UserGroupIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  PageShell,
  PremiumHero,
  PremiumSection,
  SectionHeader,
  GlassCard,
  PremiumCTA,
  inputClass,
  btnPrimaryClass,
} from '../premium/PremiumPageUI';
import { submitFormEmail } from '../../lib/submitFormEmail';

const jobListings = [
  { title: 'Senior Software Engineer', department: 'Engineering', location: 'Remote / On-site', type: 'Full-time', description: 'Build scalable enterprise applications with modern JavaScript stacks.', requirements: ['5+ years software development', 'React & TypeScript expertise', 'Cloud deployment experience'], responsibilities: ['Design and ship features', 'Mentor junior developers', 'Lead code reviews'] },
  { title: 'UX Designer', department: 'Design', location: 'Remote', type: 'Full-time', description: 'Create intuitive, premium digital experiences across web and mobile.', requirements: ['3+ years UX experience', 'Figma proficiency', 'Strong portfolio'], responsibilities: ['User research & prototyping', 'Design system contribution', 'Developer collaboration'] },
  { title: 'Product Manager', department: 'Product', location: 'On-site', type: 'Full-time', description: 'Drive product strategy aligned with business and technology goals.', requirements: ['4+ years PM experience', 'Agile methodologies', 'Stakeholder management'], responsibilities: ['Define roadmap', 'Prioritize backlog', 'Analyze market trends'] },
];

const benefits = [
  { title: 'Health & Wellness', description: 'Comprehensive medical, dental, and vision coverage.', icon: HeartIcon },
  { title: 'Work-Life Balance', description: 'Flexible hours and hybrid remote options.', icon: SparklesIcon },
  { title: 'Growth & Development', description: 'Learning budget and mentorship programs.', icon: BookOpenIcon },
  { title: 'Team Culture', description: 'Regular events and collaborative environment.', icon: UserGroupIcon },
];

const culture = [
  { emoji: '🌱', title: 'Growth Mindset', desc: 'Continuous learning and personal development.' },
  { emoji: '🤝', title: 'Collaboration', desc: 'Working together for extraordinary results.' },
  { emoji: '🎯', title: 'Innovation', desc: 'Pushing boundaries with new ideas.' },
];

const testimonials = [
  { name: 'Sarah Johnson', role: 'Senior Developer', quote: 'The culture of innovation and support is unmatched.', years: '3 years', image: 'https://randomuser.me/api/portraits/women/1.jpg' },
  { name: 'Michael Chen', role: 'Product Designer', quote: 'I have grown tremendously through mentorship here.', years: '2 years', image: 'https://randomuser.me/api/portraits/men/2.jpg' },
  { name: 'Emily Rodriguez', role: 'Product Manager', quote: 'Cutting-edge projects make every day exciting.', years: '4 years', image: 'https://randomuser.me/api/portraits/women/3.jpg' },
];

export default function Career() {
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [applicationForm, setApplicationForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    coverLetter: '',
  });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    if (!selectedJob) return;
    setIsSubmitting(true);
    try {
      await submitFormEmail({
        formName: `Career Application – ${selectedJob.title}`,
        fields: {
          position: selectedJob.title,
          department: selectedJob.department,
          location: selectedJob.location,
          fullName: applicationForm.fullName,
          email: applicationForm.email,
          phone: applicationForm.phone,
          coverLetter: applicationForm.coverLetter,
        },
        files: resume ? { resume } : {},
      });
      alert('Application submitted!');
      setShowModal(false);
      setApplicationForm({ fullName: '', email: '', phone: '', coverLetter: '' });
      setResume(null);
    } catch (error) {
      alert(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openApplicationModal = (job) => {
    setSelectedJob(job);
    setApplicationForm({ fullName: '', email: '', phone: '', coverLetter: '' });
    setResume(null);
    setShowModal(true);
  };

  const departments = ['All', ...new Set(jobListings.map((j) => j.department))];
  const locations = ['All', ...new Set(jobListings.map((j) => j.location))];
  const filtered = jobListings.filter(
    (j) => (selectedDepartment === 'All' || j.department === selectedDepartment) && (selectedLocation === 'All' || j.location === selectedLocation)
  );

  const t = testimonials[testimonialIdx];

  return (
    <PageShell>
      <PremiumHero
        badge="Careers"
        title="Join Our"
        highlight="Team"
        subtitle="Build the future of technology with passionate innovators—where your growth, impact, and ideas matter."
        stats={[
          { value: '50+', label: 'Team Members' },
          { value: '3', label: 'Open Roles' },
          { value: 'Hybrid', label: 'Work Model' },
          { value: 'Nepal', label: 'HQ' },
        ]}
        actions={[
          { label: 'View Positions', primary: true, onClick: () => document.getElementById('jobs')?.scrollIntoView({ behavior: 'smooth' }) },
          { label: 'Internship Program', href: '/internship' },
        ]}
      />

      <PremiumSection>
        <SectionHeader badge="Culture" title="Our" highlight="Values" />
        <div className="grid md:grid-cols-3 gap-6">
          {culture.map((c) => (
            <GlassCard key={c.title} className="p-8 text-center">
              <div className="text-4xl mb-4">{c.emoji}</div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-2">{c.title}</h3>
              <p className="text-sm text-slate-600 dark:text-gray-300">{c.desc}</p>
            </GlassCard>
          ))}
        </div>
      </PremiumSection>

      <PremiumSection alt>
        <SectionHeader badge="Perks" title="Why" highlight="Join Us" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {benefits.map((b) => {
            const Icon = b.icon;
            return (
              <GlassCard key={b.title} className="p-6">
                <Icon className="w-10 h-10 text-[#2563EB] mb-4" />
                <h3 className="font-bold mb-2">{b.title}</h3>
                <p className="text-sm text-slate-600 dark:text-gray-300">{b.description}</p>
              </GlassCard>
            );
          })}
        </div>
      </PremiumSection>

      <PremiumSection>
        <SectionHeader badge="Voices" title="Meet Our" highlight="Team" />
        <GlassCard className="max-w-2xl mx-auto p-10 text-center relative" hover={false}>
          <button type="button" onClick={() => setTestimonialIdx((i) => (i - 1 + testimonials.length) % testimonials.length)} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hero-glass-tag">
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <img src={t.image} alt="" className="w-20 h-20 rounded-2xl mx-auto mb-6 object-cover ring-4 ring-[#2563EB]/20" />
          <p className="text-lg italic text-slate-600 dark:text-gray-300 mb-6">&ldquo;{t.quote}&rdquo;</p>
          <h3 className="font-bold">{t.name}</h3>
          <p className="text-sm text-[#2563EB]">{t.role} · {t.years}</p>
          <button type="button" onClick={() => setTestimonialIdx((i) => (i + 1) % testimonials.length)} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full hero-glass-tag">
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </GlassCard>
      </PremiumSection>

      <PremiumSection id="jobs" alt>
        <SectionHeader badge="Opportunities" title="Open" highlight="Positions" />
        <div className="flex flex-wrap gap-4 mb-8 justify-center">
          <select value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)} className={inputClass + ' w-auto min-w-[160px]'}>
            {departments.map((d) => <option key={d} value={d}>{d === 'All' ? 'All Departments' : d}</option>)}
          </select>
          <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)} className={inputClass + ' w-auto min-w-[160px]'}>
            {locations.map((l) => <option key={l} value={l}>{l === 'All' ? 'All Locations' : l}</option>)}
          </select>
        </div>
        <div className="space-y-5">
          {filtered.map((job) => (
            <GlassCard key={job.title} className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <BriefcaseIcon className="w-6 h-6 text-[#2563EB]" />
                    {job.title}
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#2563EB]/10 text-[#2563EB]">{job.department}</span>
                    <span className="text-xs font-semibold px-3 py-1 rounded-full hero-glass-tag flex items-center gap-1"><MapPinIcon className="w-3.5 h-3.5" />{job.location}</span>
                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-700 dark:text-cyan-300">{job.type}</span>
                  </div>
                </div>
                <button type="button" onClick={() => openApplicationModal(job)} className={btnPrimaryClass + ' flex-shrink-0'}>
                  Apply Now
                </button>
              </div>
              <p className="text-sm text-slate-600 dark:text-gray-300 mb-4">{job.description}</p>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-2">Requirements</h4>
                  <ul className="space-y-1 text-slate-600 dark:text-gray-400">{job.requirements.map((r) => <li key={r}>• {r}</li>)}</ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Responsibilities</h4>
                  <ul className="space-y-1 text-slate-600 dark:text-gray-400">{job.responsibilities.map((r) => <li key={r}>• {r}</li>)}</ul>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      </PremiumSection>

      <AnimatePresence>
        {showModal && selectedJob && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="hero-glass-card rounded-3xl max-w-lg w-full p-8 relative">
              <button type="button" onClick={() => setShowModal(false)} className="absolute top-4 right-4"><XMarkIcon className="w-6 h-6" /></button>
              <h3 className="text-2xl font-bold mb-6">Apply for {selectedJob.title}</h3>
              <form className="space-y-4" onSubmit={handleApplicationSubmit}>
                <input
                  type="text"
                  placeholder="Full Name"
                  required
                  value={applicationForm.fullName}
                  onChange={(e) => setApplicationForm({ ...applicationForm, fullName: e.target.value })}
                  className={inputClass}
                />
                <input
                  type="email"
                  placeholder="Email"
                  required
                  value={applicationForm.email}
                  onChange={(e) => setApplicationForm({ ...applicationForm, email: e.target.value })}
                  className={inputClass}
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={applicationForm.phone}
                  onChange={(e) => setApplicationForm({ ...applicationForm, phone: e.target.value })}
                  className={inputClass}
                />
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className={inputClass}
                  onChange={(e) => setResume(e.target.files?.[0] || null)}
                />
                <textarea
                  rows={4}
                  placeholder="Cover letter..."
                  value={applicationForm.coverLetter}
                  onChange={(e) => setApplicationForm({ ...applicationForm, coverLetter: e.target.value })}
                  className={inputClass}
                />
                <button type="submit" disabled={isSubmitting} className={`${btnPrimaryClass} w-full disabled:opacity-60`}>
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PremiumCTA
        title="Don't See the Right Role?"
        subtitle="Send us your resume—we'll keep you in mind for future opportunities."
        primary={{ label: 'Contact Us', href: '/contact' }}
        secondary={{ label: 'Internships', href: '/internship' }}
      />
    </PageShell>
  );
}
